import type { BookingPaymentMethod, BookingPreferences, SelectedSlot } from "../bookingFlowMachine"
import { bookingIneligibilityReason, isBookingConsentRequired } from "../errorReporting"
import {
  type BookedAppointment,
  type ScreeningAppointmentType,
  type ScreeningProgram,
  useBookAppointment
} from "../hooks"
import { getDisplayTimeZone } from "../timezone"
import { useBookingTracking } from "../tracking"
import { Alert, BackIcon, Box, ForwardIcon, Typography } from "@carrotfertility/carotene-core"
import { Form, FormButton, FormTextField } from "@carrotfertility/carotene-core-x"
import React from "react"

type PaymentPageProps = {
  enabled: boolean
  paymentMethod: BookingPaymentMethod | null
  program: ScreeningProgram
  appointmentType: ScreeningAppointmentType
  preferences: BookingPreferences | null
  slot: SelectedSlot | null
  timeZone?: string
  onBack: () => void
  onConfirmBooking: (preferences: BookingPreferences, slot: SelectedSlot, appointment: BookedAppointment) => void
  onDisqualified: (exclusionMessageId: string) => void
  onNeedsConsent: () => void
}

// Temporary test booking without payment, gated by telemedicine-enterprise-payments.
// Replace this bypass with payment authorization before rollout.
export function PaymentPage({
  enabled,
  paymentMethod,
  program,
  appointmentType,
  preferences,
  slot,
  timeZone,
  onBack,
  onConfirmBooking,
  onDisqualified,
  onNeedsConsent
}: PaymentPageProps): JSX.Element {
  const bookAppointment = useBookAppointment()
  const tracking = useBookingTracking()
  const submitting = React.useRef(false)

  const handleSubmit = async () => {
    if (
      paymentMethod !== "self_pay" ||
      !enabled ||
      !preferences ||
      !slot?.availableHostIds.length ||
      submitting.current
    )
      return
    submitting.current = true
    try {
      const attemptNumber = tracking.onBookingSubmitted(appointmentType)
      const appointment = await bookAppointment.mutateAsync({
        program,
        slotToken: slot.slotToken,
        hostId: slot.availableHostIds[0],
        timeZone: getDisplayTimeZone(timeZone)
      })
      tracking.onEvent({
        type: "success",
        appointmentId: appointment.appointmentId,
        hostId: appointment.hostId,
        bookingAttemptNumber: attemptNumber
      })
      onConfirmBooking(preferences, slot, appointment)
    } catch (error) {
      const reason = bookingIneligibilityReason(error)
      if (reason !== null) {
        tracking.onEvent({ type: "gating_result", result: "ineligible", resultSource: "booking_recheck" })
        onDisqualified(reason)
      } else if (isBookingConsentRequired(error)) {
        onNeedsConsent()
      }
    } finally {
      submitting.current = false
    }
  }

  return (
    <Form onSubmit={handleSubmit} defaultValues={{ cardNumber: "", expiration: "", cvc: "", billingZip: "" }}>
      <Box
        sx={(theme) => ({
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(theme.tokens.spacing.lg),
          maxInlineSize: 640
        })}
      >
        <Typography variant="h2" component="h1">
          Add a payment method
        </Typography>
        <Alert severity="info">
          {enabled
            ? "This payment page is a placeholder. Payment fields are disabled. You can confirm your booking without making a payment."
            : "Booking a visit that requires payment is not available yet."}
        </Alert>
        {/* Display-only test fields. Replace with the payment provider's hosted inputs before collecting real card data. */}
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(theme.tokens.spacing.md),
            "& .MuiInputBase-root.Mui-disabled, & input:disabled": { cursor: "not-allowed" }
          })}
        >
          <FormTextField
            name="cardNumber"
            label="Card number"
            placeholder="1234 1234 1234 1234"
            disabled
            autoComplete="off"
            fullWidth
            inputProps={{ inputMode: "numeric", "data-heap-redact-text": true }}
          />
          <Box
            sx={(theme) => ({
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: theme.spacing(theme.tokens.spacing.sm)
            })}
          >
            <FormTextField
              name="expiration"
              label="Expiration"
              placeholder="MM / YY"
              disabled
              autoComplete="off"
              fullWidth
              inputProps={{ inputMode: "numeric", "data-heap-redact-text": true }}
            />
            <FormTextField
              name="cvc"
              label="CVC"
              placeholder="CVC"
              disabled
              autoComplete="off"
              fullWidth
              inputProps={{ inputMode: "numeric", "data-heap-redact-text": true }}
            />
            <FormTextField
              name="billingZip"
              label="Billing ZIP"
              placeholder="ZIP"
              disabled
              autoComplete="off"
              fullWidth
              inputProps={{ inputMode: "numeric", "data-heap-redact-text": true }}
            />
          </Box>
        </Box>
        {bookAppointment.isError && (
          <Alert severity="error">
            Something went wrong booking your appointment. Please try again, or go back to availability to select
            another time.
          </Alert>
        )}
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            gap: theme.spacing(theme.tokens.spacing.sm)
          })}
        >
          <FormButton
            type="button"
            onClick={onBack}
            color="secondary"
            variant="text"
            startIcon={<BackIcon />}
            disabled={bookAppointment.isLoading}
          >
            Back
          </FormButton>
          <FormButton
            type="submit"
            color="secondary"
            endIcon={<ForwardIcon />}
            disabled={
              paymentMethod !== "self_pay" ||
              !enabled ||
              !preferences ||
              !slot?.availableHostIds.length ||
              bookAppointment.isLoading
            }
          >
            Confirm booking
          </FormButton>
        </Box>
      </Box>
    </Form>
  )
}
